import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneService } from '@sc/data-access-configuration';
import {
  Appointment,
  Dossier,
  DossierCommentsEnum,
  DossierStateHistory,
  Environment,
  Fitter,
  getLatestDossierCommentTextByType,
  NotifyAction,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { DossierManagerService } from '@sc/feature-fitter-shared-libs';
import { FeatureToggleDirective } from '@sc/feature-flipping';
import { HorizonsService } from '@sc/feature-horizons';
import {
  AsteriskInfoComponent,
  ButtonComponent,
  MomentParseZonePipe,
} from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import { Subscription } from 'rxjs';
import { DossierLightUpdateErrorInstance } from '../../models/planning-dossier-update-errors';
import { PlanningCalendarService } from '../../services';
import { PlanningFormAppointmentCustomerInfoComponent } from './planning-form-appointment-customer-info/planning-form-appointment-customer-info.component';
import { PlanningFormAppointmentDocumentsComponent } from './planning-form-appointment-documents/planning-form-appointment-documents.component';
import { PlanningFormAppointmentInsuranceComponent } from './planning-form-appointment-insurance/planning-form-appointment-insurance.component';
import { PlanningFormAppointmentInterventionComponent } from './planning-form-appointment-intervention/planning-form-appointment-intervention.component';
import { PlanningFormAppointmentMobilityComponent } from './planning-form-appointment-mobility/planning-form-appointment-mobility.component';
import { ENV_CONFIG } from '@sc/data-access-environment';

@Component({
  selector: 'sc-planning-form-appointment',
  styleUrls: ['./planning-form-appointment.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FeatureToggleDirective,
    NgIf,
    ButtonComponent,
    MatFormFieldModule,
    NgFor,
    PlanningFormAppointmentCustomerInfoComponent,
    PlanningFormAppointmentInterventionComponent,
    PlanningFormAppointmentMobilityComponent,
    PlanningFormAppointmentInsuranceComponent,
    PlanningFormAppointmentDocumentsComponent,
    AsyncPipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
    AsteriskInfoComponent,
  ],
  template: `
    <div class="planning-form-title">
      <h1>
        {{
          (dossier?.id
            ? 'PlanningFormAppointment.title.edition'
            : 'PlanningFormAppointment.title.creation'
          ) | translate
        }}
      </h1>

      <div class="planning-form-header-button-section">
        <span *scFeatureToggle="'enable-horizons-sync'">
          <sc-button
            *ngIf="env.envName === 'dev2' && dossier?.id"
            (click)="openInHorizons(dossier?.externalReferences?.horizonsId)"
            type="button"
            leftIcon="external_link"
            [disabled]="!dossier?.externalReferences?.horizonsId"
          >
            {{ 'PlanningFormAppointment.openInHorizons' | translate }}
          </sc-button>
        </span>

        <span *ngIf="dossier.id" class="notification-center-btn">
          <sc-button (click)="printDossier()" type="button" leftIcon="printer">
            {{ 'PlanningFormAppointment.printButton' | translate }}
          </sc-button>

          <mat-hint *ngIf="wasFormChanged">{{
            'PlanningFormAppointment.printSaveButtonHint' | translate
          }}</mat-hint>
        </span>
      </div>
    </div>
    <sc-asterisk-info></sc-asterisk-info>

    <div *ngIf="displayedErrors?.length" class="planning-form-box alert">
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.cannotModifyWithoutFormTitle' | translate
        }}</span>
        <ul>
          <li *ngFor="let error of displayedErrors">
            {{
              'PlanningFormAppointment.errorsOnFormOpen.' + error?.errorType
                | translate: error?.displayInterpolationParams
            }}
          </li>
        </ul>
      </div>
    </div>

    <div *ngIf="isCancelledAppointment()" class="planning-form-box alert">
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.cancelAlertTitle'
            | translate: { isRequestAppointment: isRequestAppointment() }
        }}</span>
        <span
          [innerHTML]="
            getCancelKey(getCancelEvent())
              | translate
                : {
                    userType: getUserType(getCancelEvent()),
                    userName: getCancelEvent()?.userName,
                    isRequestAppointment: isRequestAppointment(),
                    dateCancel:
                      (getCancelEvent()?.timestamp
                      | parseZoneTimeString
                        : timezoneService.getFitterTimezone(
                            getFitter(dossier?.appointment)
                          )
                      | localeDate: 'shortDate'
                      | async)
                  }
          "
        ></span>
        <span *ngIf="getComment()">{{
          'PlanningFormAppointment.cancelAlertInfosReasonLabel'
            | translate
              : {
                  cancelReason: getComment()
                }
        }}</span>
      </div>
    </div>

    <div *ngIf="isAbandonedDossier()" class="planning-form-box alert">
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.abandonAlertTitle' | translate
        }}</span>
        <span
          [innerHTML]="
            'PlanningFormAppointment.abandonAlertInfos'
              | translate
                : {
                    dateAbandon:
                      (getAbandonDate()
                      | parseZoneTimeString
                        : timezoneService.getFitterTimezone(
                            getFitter(dossier?.appointment)
                          )
                      | localeDate: 'shortDate'
                      | async)
                  }
          "
        ></span>
        <span>{{
          'PlanningFormAppointment.abandonAlertComment'
            | translate: { abandonComment: getAbandonComment() }
        }}</span>
      </div>
    </div>

    <div *ngIf="isNoShowAppointment()" class="planning-form-box alert">
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.noShowAlertTitle' | translate
        }}</span>
      </div>
      <div class="alert-action-container">
        <sc-button
          leftIcon="mail"
          buttonType="error"
          [disabled]="!canNotifyNoShow() || isBlockedByConcurency()"
          (click)="onInformCustomerClick('NO_SHOW')"
          >{{
            (canNotifyNoShow()
              ? 'PlanningFormAppointment.informCustomerBtn'
              : 'PlanningFormAppointment.informCustomerBtnClicked'
            ) | translate
          }}</sc-button
        >
      </div>
    </div>

    <div *ngIf="canNotifyModif()" class="planning-form-box info">
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.modifyInfosTitle' | translate
        }}</span>
        <ng-container *ngIf="getLastAppointment()">
          <span
            >{{ 'PlanningFormAppointment.initialAppointment' | translate }}
            <strong>{{
              getAppointmentDateKey(getLastAppointment())
                | translate
                  : {
                      day:
                        getLastAppointment()?.time
                        | parseZoneTimeString
                          : timezoneService.getFitterTimezone(
                              getFitter(dossier?.appointment)
                            )
                        | localeDate: 'fullDate'
                        | async,
                      startTime:
                        getLastAppointment()?.time
                        | parseZoneTimeString
                          : timezoneService.getFitterTimezone(
                              getFitter(dossier?.appointment)
                            )
                        | localeDate: 'shortTime'
                        | async,
                      endTime:
                        getLastAppointment()?.endTime
                        | parseZoneTimeString
                          : timezoneService.getFitterTimezone(
                              getFitter(dossier?.appointment)
                            )
                        | localeDate: 'shortTime'
                        | async
                    }
            }}</strong></span
          >
          <span
            >{{ 'PlanningFormAppointment.newAppointment' | translate }}
            <strong>{{
              getAppointmentDateKey(dossier?.appointment)
                | translate
                  : {
                      day:
                        dossier?.appointment?.time
                        | parseZoneTimeString
                          : timezoneService.getFitterTimezone(
                              getFitter(dossier?.appointment)
                            )
                        | localeDate: 'fullDate'
                        | async,
                      startTime:
                        dossier?.appointment?.time
                        | parseZoneTimeString
                          : timezoneService.getFitterTimezone(
                              getFitter(dossier?.appointment)
                            )
                        | localeDate: 'shortTime'
                        | async,
                      endTime:
                        dossier?.appointment?.endTime
                        | parseZoneTimeString
                          : timezoneService.getFitterTimezone(
                              getFitter(dossier?.appointment)
                            )
                        | localeDate: 'shortTime'
                        | async
                    }
            }}</strong></span
          >
        </ng-container>
      </div>
      <div class="action-container">
        <sc-button
          leftIcon="mail"
          [disabled]="isNotified || form?.disabled"
          (click)="onInformCustomerClick('MODIFICATION')"
          >{{
            (!isNotified
              ? 'PlanningFormAppointment.informCustomerBtn'
              : 'PlanningFormAppointment.informCustomerBtnClicked'
            ) | translate
          }}</sc-button
        >
      </div>
    </div>
    <div *ngIf="isAppointmentDone()" class="planning-form-box done">
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.doneInfosTitle' | translate
        }}</span>
        <span>{{ 'PlanningFormAppointment.doneInfosText' | translate }} </span>
      </div>
      <div class="action-container">
        <sc-button
          leftIcon="mail"
          buttonType="validation"
          [disabled]="!canNotifyDone()"
          (click)="onInformCustomerClick('COMPLETED')"
          >{{
            (canNotifyDone()
              ? 'PlanningFormAppointment.informCustomerBtn'
              : 'PlanningFormAppointment.informCustomerBtnClicked'
            ) | translate
          }}</sc-button
        >
      </div>
    </div>
    <div
      *ngIf="isReplacementVehicleUnavailable()"
      class="planning-form-box alert"
    >
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.replacementVehicleUnavailableTitle'
            | translate
        }}</span>
        <span
          >{{
            'PlanningFormAppointment.replacementVehicleUnavailableText'
              | translate
          }}
        </span>
      </div>
    </div>

    <div
      *ngIf="displayReplacementVehicleNeededAlert()"
      class="planning-form-box alert"
    >
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.replacementVehicleNeededTitle' | translate
        }}</span>
        <span
          >{{
            'PlanningFormAppointment.replacementVehicleNeededText' | translate
          }}
        </span>
      </div>
    </div>

    <div *ngIf="isBlockedByConcurency()" class="planning-form-box info">
      <div class="infos-container">
        <span class="infos-title">{{
          'PlanningFormAppointment.workingOnDossierAlertTitle' | translate
        }}</span>
        <span
          [innerHTML]="
            'PlanningFormAppointment.workingOnDossierAlertInfos'
              | translate: { userName: dossier.currentlyInUse?.userName }
          "
        ></span>
      </div>
    </div>

    <sc-planning-form-appointment-customer-info
      id="form-customer-info"
      [form]="form"
      [touched]="touched"
      (loadDossierEvent)="loadDossier($event)"
    ></sc-planning-form-appointment-customer-info>

    <sc-planning-form-appointment-intervention
      id="form-apmt-intervention"
      [form]="form"
      [fitters]="fitters"
      [dossier]="dossier"
      [touched]="touched"
    ></sc-planning-form-appointment-intervention>

    <sc-planning-form-appointment-mobility
      id="form-apmt-mobility"
      [initForm]="form"
      [fitters]="fitters"
      [_dossier]="dossier"
      [touched]="touched"
      (isSelectedReplacementVehicleErrorEvent)="
        onSelectedReplacementVehicleError($event)
      "
    >
    </sc-planning-form-appointment-mobility>

    <sc-planning-form-appointment-insurance
      id="form-apmt-insurance"
      [form]="form"
      [touched]="touched"
    ></sc-planning-form-appointment-insurance>

    <sc-planning-form-appointment-documents
      id="form-apmt-documents"
      [form]="form"
      [dossier]="dossier"
      [touched]="touched"
      (sendCustomerEmailDocumentUpload)="
        notifyAppointmentEvent.emit('CUSTOMER_DOCUMENT_UPLOAD_EMAIL')
      "
      (sendCustomerSMSDocumentUpload)="
        notifyAppointmentEvent.emit('CUSTOMER_DOCUMENT_UPLOAD_SMS')
      "
    >
    </sc-planning-form-appointment-documents>
  `,
})
export class PlanningFormAppointmentComponent implements OnInit {
  @Input() form: FormGroup;
  @Input() fitters: Fitter[];
  @Input() dossier: Dossier;
  @Input() forceEditMode: boolean;
  @Input() isNotified: boolean;
  @Input() touched: boolean;
  @Input() set errors(newErrors: DossierLightUpdateErrorInstance[]) {
    this.displayedErrors = newErrors;
  }
  @Input() disablePrint = false;
  @Input() wasFormChanged = false;

  @Output() notifyAppointmentEvent = new EventEmitter<NotifyAction>();
  @Output() loadDossierEvent = new EventEmitter<Dossier>();
  @Output() printDossierEvent = new EventEmitter<Dossier>();

  replacementVehicleUnavailable = false;
  replacementVehicleNeeded = false;
  replacementVehicleSelected = false;

  displayedErrors: DossierLightUpdateErrorInstance[];
  removeErrorsSubs: Subscription;

  constructor(
    private readonly planningService: PlanningCalendarService,
    private readonly translator: TranslatorService,
    public timezoneService: TimezoneService,
    private readonly dossierManagerService: DossierManagerService,
    private readonly horizonsService: HorizonsService,
    @Inject(ENV_CONFIG) public env: Environment
  ) {}

  ngOnInit(): void {
    const controlReplacementVehicleToggle = this.form
      .get('appointment')
      ?.get('isReplacementVehicleSelected');

    const controlReplacementVehicleSelect = this.form
      .get('appointment')
      ?.get('assignedReplacementVehicle');

    this.replacementVehicleNeeded = !!controlReplacementVehicleToggle?.value;
    this.replacementVehicleSelected = !!controlReplacementVehicleSelect?.value;

    controlReplacementVehicleSelect?.valueChanges.subscribe(
      (newReplacementVehicle) => {
        this.replacementVehicleSelected = !!newReplacementVehicle;
      }
    );
  }

  getComment() {
    return getLatestDossierCommentTextByType(this.dossier, [
      DossierCommentsEnum.appointment,
    ]);
  }

  printDossier() {
    const dossier = {
      ...this.dossier,
      ...this.form.value,
      appointment: {
        ...this.dossier?.appointment,
        ...this.form.value.appointment,
        status: this.dossier?.appointment?.status,
      },
    } as Dossier;
    this.printDossierEvent.emit(dossier);
  }

  openInHorizons(horizonsId: string) {
    const horizonsDossierUrl =
      this.horizonsService.getHorizonsDossierUrl(horizonsId);

    window.open(horizonsDossierUrl, 'horizons_window');
  }

  loadDossier(dossier: Dossier) {
    this.loadDossierEvent.emit(dossier);
  }

  isRequestAppointment() {
    return !!this.dossier?.requestAppointment && !this.dossier?.appointment;
  }

  isCancelledAppointment() {
    return (
      this.planningService.isDossierCancelled(this.dossier) &&
      !this.isAbandonedDossier()
    );
  }

  wasJustCancelledAppointment() {
    if (
      this.dossier?.stateHistory?.length > 1 &&
      this.getInvertedStateHistory()[1]?.state === 'CANCELLED' &&
      this.dossier?.updateDate === this.getInvertedStateHistory()[0]?.timestamp
    ) {
      return true;
    }

    return false;
  }

  getCancelEvent(): DossierStateHistory {
    return this.getInvertedStateHistory()?.find(
      (stateInfos) => stateInfos.state === 'CANCELLED'
    );
  }

  getCancelKey(state: DossierStateHistory): string {
    if (state?.userName) {
      if (state?.userType === 'FITTER' || state?.userType === 'API') {
        return 'PlanningFormAppointment.cancelAlertInfosUserNameOnly';
      } else {
        return 'PlanningFormAppointment.cancelAlertInfosUserName';
      }
    } else {
      return 'PlanningFormAppointment.cancelAlertInfosUserType';
    }
  }

  getUserType(state: DossierStateHistory): string {
    return state?.userType
      ? this.translator.getInstantTranslation(
          `GlobalApplicativeConstants.userType.${state.userType}`
        )
      : this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.notApplicable'
        );
  }

  isAbandonedDossier() {
    return this.planningService.isDossierAbandoned(this.dossier);
  }

  isAppointmentDone() {
    return this.planningService.isAppointmentDone(this.dossier);
  }

  getAbandonDate() {
    return this.getInvertedStateHistory()?.find(
      (stateInfos) => stateInfos.state === 'ABANDONED'
    )?.timestamp;
  }

  getAbandonComment() {
    return getLatestDossierCommentTextByType(this.dossier, [
      DossierCommentsEnum.abandon,
    ]);
  }

  getInvertedStateHistory() {
    return [...(this.dossier?.stateHistory || [])].reverse();
  }

  isNoShowAppointment() {
    return this.dossier?.state === 'NO_SHOW' && !this.isCancelledAppointment();
  }

  isModifiedAppointment() {
    return (
      !!this.dossier?.previousAppointments?.length &&
      !this.isCancelledAppointment() &&
      !this.isAbandonedDossier() &&
      !this.isNoShowAppointment() &&
      !this.wasJustCancelledAppointment()
    );
  }

  canNotifyNoShow() {
    return (
      !!this.dossier?.canNotifyCustomer &&
      !this.isNotified &&
      this.isNoShowAppointment()
    );
  }

  canNotifyModif() {
    return (
      dayjs().isBefore(this.dossier?.appointment?.time) &&
      !!this.dossier?.canNotifyCustomer &&
      !this.isCancelledAppointment() &&
      !this.isAbandonedDossier() &&
      !this.isNoShowAppointment() &&
      !this.isAppointmentDone() &&
      !this.wasJustCancelledAppointment()
    );
  }

  canNotifyDone() {
    return (
      !!this.dossier?.canNotifyCustomer &&
      !this.isNotified &&
      this.isAppointmentDone()
    );
  }

  getAppointmentDateKey(appointment: Appointment) {
    return appointment?.endTime
      ? 'PlanningFormAppointment.dateSingleDay'
      : 'PlanningFormAppointment.dateSingleDayNoEnd';
  }

  getLastAppointment() {
    return this.dossier?.previousAppointments?.[
      this.dossier?.previousAppointments?.length - 1
    ];
  }

  getFitter(appointment: Appointment) {
    return this.fitters.filter(
      (fitter) => fitter.id === appointment?.fitterId
    )[0];
  }

  isBlockedByConcurency() {
    return this.dossierManagerService.dossierOpennedBySomeoneElse(this.dossier);
  }

  onInformCustomerClick(action: NotifyAction) {
    this.isNotified = true;

    let usedAction = action;
    if (action === 'MODIFICATION') {
      const nbPreviousApmt = this.dossier.previousAppointments?.length;
      if (
        !nbPreviousApmt ||
        !dayjs(this.dossier.previousAppointments[nbPreviousApmt]?.time).isSame(
          this.dossier.appointment?.time
        )
      ) {
        usedAction = 'BOOK';
      }
    }

    this.notifyAppointmentEvent.emit(usedAction);
  }

  onSelectedReplacementVehicleError(event) {
    this.replacementVehicleUnavailable = event;
  }

  isReplacementVehicleUnavailable() {
    return this.isModifiedAppointment() && this.replacementVehicleUnavailable;
  }

  displayReplacementVehicleNeededAlert() {
    return (
      (!this.dossier?.id || !this.dossier?.appointment?.isValidatedByFitter) &&
      this.replacementVehicleNeeded &&
      !this.replacementVehicleSelected
    );
  }
}
